import { r, s } from '@/theme/tokens';
import { AdapterDayjs, LocalizationProvider, MuiDatePicker } from './datePickers';
import { FormField } from './FormField';
import { resolveFormFieldBackgroundColor, type FormFieldStyleVariant } from './formFieldVariants';
import type { SxProps, Theme } from './types';
import type { Dayjs } from 'dayjs';
import type { ReactNode } from 'react';
import 'dayjs/locale/en-gb';

export type DatePickerProps = {
  label?: ReactNode;
  inlineLabel?: boolean;
  helpText?: ReactNode;
  errorText?: ReactNode;
  required?: boolean;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  fieldStyle?: FormFieldStyleVariant;
  inputBackgroundColor?: 'background.paper' | 'background.default';
  placeholder?: string;
  textFieldSx?: SxProps<Theme>;
  openPickerButtonSx?: SxProps<Theme>;
  calendarPaperSx?: SxProps<Theme>;
  locale?: string;
  format?: string;
  lowercasePlaceholder?: boolean;
};

export const DatePicker = ({
  label,
  inlineLabel = false,
  helpText,
  errorText,
  required,
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  fieldStyle = 'default',
  inputBackgroundColor,
  placeholder,
  textFieldSx,
  openPickerButtonSx,
  calendarPaperSx,
  locale,
  format,
  lowercasePlaceholder = false,
}: DatePickerProps): JSX.Element => {
  const resolvedInputBackgroundColor = resolveFormFieldBackgroundColor(
    fieldStyle,
    inputBackgroundColor,
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale ?? 'en-gb'}>
      <FormField
        label={inlineLabel ? undefined : label}
        helpText={helpText}
        errorText={errorText}
        required={required}
      >
        {(props) => {
          const { label: _label, labelId: _labelId, ...fieldProps } = props;
          const resolvedLocale = locale ?? 'en-gb';
          const resolvedFormat =
            format ??
            (resolvedLocale.toLowerCase().endsWith('gb')
              ? 'DD/MM/YYYY'
              : resolvedLocale.toLowerCase().startsWith('en')
                ? 'MM/DD/YYYY'
                : undefined);

          return (
            <MuiDatePicker
              value={value}
              onChange={onChange}
              minDate={minDate}
              maxDate={maxDate}
              disabled={disabled}
              format={resolvedFormat}
              slotProps={{
                textField: {
                  ...fieldProps,
                  fullWidth: true,
                  required,
                  error: Boolean(errorText),
                  size: 'small',
                  label: inlineLabel ? label : undefined,
                  helperText: undefined,
                  placeholder,
                  InputLabelProps: inlineLabel ? { shrink: true } : undefined,
                  inputProps: {
                    ...(fieldProps as { inputProps?: Record<string, unknown> }).inputProps,
                    placeholder,
                  },
                  sx: [
                    {
                      '& .MuiPickersInputBase-root': {
                        paddingLeft: s('sm'),
                        backgroundColor: resolvedInputBackgroundColor,
                      },
                      '& .MuiPickersOutlinedInput-root': {
                        paddingLeft: s('sm'),
                        backgroundColor: resolvedInputBackgroundColor,
                      },
                      '& .MuiPickersInputBase-sectionsContainer': { paddingLeft: s('sm') },
                      '& .MuiPickersSectionList-root': { paddingLeft: s('sm') },
                      '& .MuiPickersInputBase-input': {
                        paddingLeft: s('sm'),
                        fontSize: (theme: Theme) => theme.typography.subtitle1.fontSize,
                        lineHeight: (theme: Theme) => theme.typography.subtitle1.lineHeight,
                      },
                      '& .MuiPickersInputBase-sectionContent': {
                        fontSize: (theme: Theme) => theme.typography.subtitle1.fontSize,
                        lineHeight: (theme: Theme) => theme.typography.subtitle1.lineHeight,
                        ...(lowercasePlaceholder ? { textTransform: 'lowercase' } : {}),
                      },
                    },
                    ...(textFieldSx
                      ? Array.isArray(textFieldSx)
                        ? textFieldSx
                        : [textFieldSx]
                      : []),
                  ],
                },
                openPickerButton: {
                  size: 'small',
                  sx: [
                    {
                      pl: 0,
                      ml: `-${s('sm')}`,
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.1rem',
                        mr: s('xs'),
                      },
                    },
                    ...(openPickerButtonSx
                      ? Array.isArray(openPickerButtonSx)
                        ? openPickerButtonSx
                        : [openPickerButtonSx]
                      : []),
                  ],
                },
                desktopPaper: {
                  sx: [
                    {
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: `${r('md')} !important`,
                      boxShadow: 'none',
                      overflow: 'hidden',
                    },
                    ...(calendarPaperSx
                      ? Array.isArray(calendarPaperSx)
                        ? calendarPaperSx
                        : [calendarPaperSx]
                      : []),
                  ],
                },
                mobilePaper: {
                  sx: [
                    {
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: `${r('md')} !important`,
                      boxShadow: 'none',
                      overflow: 'hidden',
                    },
                    ...(calendarPaperSx
                      ? Array.isArray(calendarPaperSx)
                        ? calendarPaperSx
                        : [calendarPaperSx]
                      : []),
                  ],
                },
                popper: {
                  sx: [
                    {
                      '& .MuiPaper-root': {
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: `${r('md')} !important`,
                        boxShadow: 'none',
                        overflow: 'hidden',
                      },
                      '& .MuiPickersLayout-root': {
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiDayCalendar-root, & .MuiPickersCalendarHeader-root, & .MuiPickersArrowSwitcher-root':
                        {
                          backgroundColor: 'background.paper',
                        },
                    },
                    ...(calendarPaperSx
                      ? Array.isArray(calendarPaperSx)
                        ? calendarPaperSx
                        : [calendarPaperSx]
                      : []),
                  ],
                },
              }}
            />
          );
        }}
      </FormField>
    </LocalizationProvider>
  );
};
